/**
 * FieryHotFolderSection — Fiery DTF hot folder, reported per print PC (BLU-233).
 *
 * The hot folder is a MACHINE-LOCAL path on each print/RIP PC, picked with the
 * Print Agent's native folder browser on that machine (agent panel → Fiery hot
 * folder → Browse). Each PC's pick is shadow-written to
 * `locations.production_settings.fiery_hot_folder` as a per-installation map,
 * and the backend serves each agent ONLY its own entry — so two PCs at one
 * location never clobber each other (the China-testing fix).
 *
 * This section is therefore READ-ONLY: it shows what each PC has reported, with
 * a Clear action that wipes the reported shadow values (display-only — each
 * PC's local pick is untouched and keeps working).
 */
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  Film,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

interface LocationRow {
  id: string;
  name: string;
  fieryHotFolder: string | null;
}

interface FieryHotFolderRowProps {
  location: LocationRow;
  onClear: (locationId: string) => Promise<void>;
  isSaving: boolean;
  /** False in Console single-location mode — the section header already names the location. */
  showName?: boolean;
}

function FieryHotFolderRow({ location, onClear, isSaving, showName = true }: FieryHotFolderRowProps) {
  const { t } = useTranslation('settings');
  const hasValue = location.fieryHotFolder !== null;

  const handleClear = async () => {
    try {
      await onClear(location.id);
    } catch {
      // Toast already fired.
    }
  };

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors"
      role="listitem"
    >
      <div className="flex items-center gap-2 min-w-0">
        {showName && <span className="font-medium text-sm truncate">{location.name}</span>}
        {hasValue ? (
          <Badge variant="info" className="shrink-0">
            {t('production.fieryHotFolder.reported')}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-muted-foreground border-muted-foreground/30 font-normal shrink-0"
          >
            {t('production.fieryHotFolder.notConfigured')}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        <span
          className={cn(
            'text-sm font-mono truncate max-w-[420px]',
            hasValue ? 'text-foreground' : 'text-muted-foreground italic',
          )}
          title={location.fieryHotFolder ?? undefined}
        >
          {hasValue ? location.fieryHotFolder : t('production.fieryHotFolder.noneItalic')}
        </span>
        {hasValue && (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-muted-foreground hover:text-foreground"
            onClick={() => void handleClear()}
            disabled={isSaving}
            aria-label={t('production.fieryHotFolder.clearAriaLabel', { location: location.name })}
            title={t('production.fieryHotFolder.clearTooltip')}
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RotateCcw className="h-3.5 w-3.5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

interface FieryHotFolderSectionProps {
  /**
   * Console single-location mode: when set, render only this location's row
   * and omit the per-row location name + the "Locations" summary header (the
   * Console section header already names the location). Absent = today's
   * full-list behavior.
   */
  locationId?: string;
}

export function FieryHotFolderSection({ locationId }: FieryHotFolderSectionProps = {}) {
  const { t } = useTranslation('settings');
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.productionSettings.updateLocationFieryHotFolder.useMutation({
    onSuccess: (_data, variables) => {
      const loc = locationsQuery.data?.find((l) => l.id === variables.locationId);
      const locName = loc?.name ?? t('production.fieryHotFolder.locationFallback');
      toast.success(t('production.fieryHotFolder.toastCleared', { location: locName }));
      void utils.productionSettings.getLocationSettings.invalidate();
    },
    onError: (error) => {
      toast.error(t('production.fieryHotFolder.toastFailedDetail', { message: error.message }));
    },
  });

  const pendingLocationId =
    updateMutation.isPending ? updateMutation.variables?.locationId ?? null : null;

  const productionLocations: LocationRow[] = (locationsQuery.data ?? [])
    .filter((l) => l.productionEnabled && (!locationId || l.id === locationId))
    .map((l) => ({
      id: l.id,
      name: l.name,
      fieryHotFolder: l.fieryHotFolder ?? null,
    }));

  const handleClear = (locationId: string) =>
    updateMutation.mutateAsync({ locationId, fieryHotFolder: null }).then(() => undefined);

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <Film className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">
            {t('production.fieryHotFolder.description')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('production.fieryHotFolder.pickerHint')}
          </p>
        </div>
      </div>

      {locationsQuery.isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('production.fieryHotFolder.loadFailedTitle')}</AlertTitle>
          <AlertDescription className="flex items-center gap-2">
            {locationsQuery.error?.message ?? t('production.fieryHotFolder.loadFailedBody')}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => void locationsQuery.refetch()}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              {t('production.fieryHotFolder.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {locationsQuery.isLoading && (
        <div className="flex items-center justify-center py-6" role="status" aria-busy="true">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            {t('production.fieryHotFolder.loading')}
          </span>
        </div>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length === 0 && (
        <p className="text-sm text-muted-foreground py-4">
          {t('production.fieryHotFolder.noLocations')}
        </p>
      )}

      {!locationsQuery.isLoading && !locationsQuery.isError && productionLocations.length > 0 && (
        <div className="space-y-3">
          {!locationId && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
              <div>
                <h4 className="font-medium text-base">{t('production.fieryHotFolder.locationsTitle')}</h4>
                <p className="text-xs text-muted-foreground">
                  {t('production.fieryHotFolder.locationsHelp')}
                </p>
              </div>
            </div>
          )}
          <div className="space-y-1" role="list" aria-label={t('production.fieryHotFolder.listAria')}>
            {productionLocations.map((location) => (
              <FieryHotFolderRow
                key={location.id}
                location={location}
                onClear={handleClear}
                isSaving={pendingLocationId === location.id}
                showName={!locationId}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
